/**
 * TopNav - Desktop top navigation header.
 *
 * Fixed to top on desktop (>=768px), hidden on mobile.
 * Logo/brand on left, navigation items horizontally, NotificationBell and LanguageSwitcher on right.
 * Admin link visible only when user.role === 'admin'.
 *
 * Requirements: 22.2, 22.3, 22.4, 22.5, 25.3
 */

import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth, redirectToLineOAuth } from '@/contexts/AuthContext';
import { isLiff } from '@/services/environment';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NotificationBell } from './NotificationBell';

interface NavItem {
  path: string;
  labelKey: string;
}

const navItems: NavItem[] = [
  { path: '/', labelKey: 'nav.home' },
  { path: '/browse', labelKey: 'nav.browse' },
  { path: '/post', labelKey: 'nav.post' },
  { path: '/my-listings', labelKey: 'nav.myListings' },
  { path: '/saved', labelKey: 'nav.saved' },
  { path: '/help', labelKey: 'nav.help' },
  { path: '/profile', labelKey: 'nav.profile' },
];

export function TopNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const inLiff = isLiff();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 hidden border-b border-gray-200 bg-white shadow-sm md:block"
      role="banner"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo / Brand */}
        <NavLink to="/" className="flex items-center gap-2">
          <span className="text-xl font-bold text-primary-700">FindoryHub</span>
        </NavLink>

        {/* Navigation Items */}
        <nav aria-label="Main navigation">
          <ul className="flex items-center gap-1">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    aria-current={active ? 'page' : undefined}
                    {...(inLiff ? { target: '_self' } : {})}
                  >
                    {t(item.labelKey)}
                  </NavLink>
                </li>
              );
            })}

            {/* Admin link - only visible to admin users */}
            {user?.role === 'admin' && (
              <li>
                <NavLink
                  to="/admin"
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-secondary-50 text-secondary-700'
                      : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-700'
                  }`}
                  aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
                >
                  {t('nav.admin')}
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* Right side: Login/Profile + Notifications + Language Switcher */}
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell />
              <NavLink
                to="/profile"
                className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                  {user.displayName?.charAt(0) || '?'}
                  {user.profileImageUrl && (
                    <img
                      src={user.profileImageUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full rounded-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                </span>
                <span className="hidden lg:inline">{user.displayName}</span>
              </NavLink>
            </>
          ) : (
            <button
              type="button"
              onClick={() => redirectToLineOAuth()}
              className="min-h-[44px] rounded-md bg-[#06C755] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#05b04c]"
            >
              {t('auth.login')}
            </button>
          )}
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
